import Modal from 'antd/lib/modal/Modal';
import Button from 'antd/lib/button';
import React, { useMemo, useState, useEffect } from 'react';
import { IEvent } from '../../../domain/event/interface';
import { useEventListQuery } from '../../../domain/event/queries';
import dayjs from 'dayjs';
import { HistoricalPerformanceChart, IHistoricalPerformanceChartData } from './HistoricalPerformanceChart';
import { ChartPagination } from './ChartPagination';
import { isNumber } from 'lodash';
import App from 'antd/lib/app';
import Flex from 'antd/lib/flex';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';
import Typography from 'antd/lib/typography';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import { getComputerFormattedTime } from '../../../domain/date/date';
import './historical-performance-modal.css';

export interface IHistoricalPerformanceChartModalConfig {
    sourceEventId: number;
    siteId: number;
    siteName: string;
    programName: string;
    san?: string;
}

interface IHistoricalPerformanceChartModalProps {
    config: IHistoricalPerformanceChartModalConfig;
    onClose: () => void;
}

export const HistoricalPerformanceChartModal = ({ onClose, config }: IHistoricalPerformanceChartModalProps) => {
    const handleClose = () => onClose();
    const [modalReady, setModalReady] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number | null>(null);
    const itemsPerPage = 7;
    const { notification } = App.useApp();

    const handleModalState = (isOpen: boolean) => {
        if (isOpen) {
            setModalReady(true);
        }
    };

    const { data, isFetching, isError, error } = useEventListQuery({
        siteId: config.siteId,
        start: dayjs().year(2024).startOf('year').toDate(),
        sourceProgram: config.programName,
        include: 'event_analytic',
        sorter: {
            field: 'event_datetime_start',
            order: 'ascend',
        },
        pagination: {
            pageSize: 10000,
            current: 1,
        },
    });

    if (isError) {
        notification.error({
            key: 'event-analytics-historical-performance-error',
            message: error?.message || 'Cannot fetch usage data for events!',
        });
    }

    const preparedData: IHistoricalPerformanceChartData[] | null = useMemo(() => {
        const events: IEvent[] = data?.data?.length ? data.data : [];
        if (!events.length || !config) return null;

        return events.map(event => {
            let eventAnalytic = event.analytic || [];

            if (config.san) {
                eventAnalytic = eventAnalytic.filter(item => item.service_account_number === config.san);
            }

            const performanceValues = eventAnalytic.map(item => item.performance_kw);
            const baselineValues = eventAnalytic.map(item => item.baseline_kw);
            const nominationValues = eventAnalytic.map(item => item.nomination_kw);

            return {
                initialEvent: event.event_id === config.sourceEventId,
                performanceKw: sumValues(performanceValues),
                nominationKw: sumValues(nominationValues),
                baselineKw: sumValues(baselineValues),
                eventStart: event.event_datetime_start,
                eventDuration: event.event_duration,
                meters: eventAnalytic.length,
            };
        });
    }, [data, config]);

    const paginatedData = useMemo(() => {
        if (!preparedData || currentPage === null) return null;

        const firstPageItems = preparedData.length % itemsPerPage;
        const firstPageItemsOffset = firstPageItems ? itemsPerPage - firstPageItems : 0;

        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        // For inverted pagination where the last page always first (to display latest items)
        // We want to show max items on that page
        const adjustedStartIndex = startIndex - firstPageItemsOffset;
        const adjustedEndIndex = endIndex - firstPageItemsOffset;

        return preparedData.slice(adjustedStartIndex > 0 ? adjustedStartIndex : 0, adjustedEndIndex);
    }, [preparedData, currentPage]);

    useEffect(() => {
        if (currentPage !== null || !preparedData?.length) return;

        const totalPages = Math.ceil(preparedData.length / itemsPerPage);

        setCurrentPage(totalPages);
    }, [preparedData, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const timezone = getComputerFormattedTime(new Date(), 'xxx');
    const chartTimezone = getComputerFormattedTime(new Date(), 'zzz');

    return (
        <Modal
            className="historical-performance-chart-modal"
            open
            width={{
                xs: '90%',
                sm: '90%',
                md: '90%',
                lg: '90%',
                xl: '1200px',
                xxl: '1200px',
            }}
            height={600}
            title={getTitle(config.siteName, config.programName, config.san)}
            destroyOnHidden
            closeIcon
            afterOpenChange={handleModalState}
            onCancel={handleClose}
            styles={{
                footer: {
                    borderTop: 'none',
                    padding: '0 24px 24px 24px',
                },
            }}
            footer={[
                currentPage ? (
                    <ChartPagination
                        key="pagination"
                        totalItems={data?.data?.length || 0}
                        itemsPerPage={itemsPerPage}
                        defaultCurrentPage={currentPage}
                        onChange={handlePageChange}
                    />
                ) : null,
                <div key="spacer" style={{ padding: '0 4px' }} />,
                <Button key="modal-cancel" onClick={handleClose}>
                    Close
                </Button>,
            ]}
        >
            <div className="historical-performance-modal-content">
                {(isFetching || !modalReady || !paginatedData) && (
                    <Flex style={{ height: '100%' }} align="center" justify="center">
                        <Spin />
                    </Flex>
                )}
                {isError && (
                    <Flex style={{ height: '100%' }} align="center" justify="center">
                        <Empty />
                    </Flex>
                )}
                {modalReady && !isFetching && paginatedData && (
                    <>
                        {chartTimezone && (
                            <Typography.Text style={{ display: 'block', color: 'darkgrey', marginBottom: 12, textAlign: 'center' }}>
                                <InfoCircleOutlined /> Times on this chart are displayed in your computer&apos;s timezone - {chartTimezone}
                            </Typography.Text>
                        )}
                        <HistoricalPerformanceChart data={paginatedData} timezone={timezone} />
                    </>
                )}
            </div>
        </Modal>
    );
};

function sumValues(values: (number | null)[]): number | null {
    const numberValues = values.filter(value => isNumber(value)) as number[];

    if (!numberValues.length) return null;

    return numberValues.reduce((a, b) => a + b, 0);
}

function getTitle(siteName: string, programName: string, san?: string): string {
    if (san) {
        return `Event's Performance History for Meter: "${san}", Site: "${siteName}", Program: "${programName}"`;
    }

    return `Event's Performance History for  Site: "${siteName}", Program: "${programName}"`;
}
